import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteIcon from '@mui/icons-material/Delete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CardMedia from '@mui/material/CardMedia';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import ImageDisplayFields from 'src/common/components/ImageDisplayFields';
import MediaManagerModal from 'src/common/components/modals/MediaManagerModal';
import StackedGalleryThumbnail from 'src/common/components/page-blocks/StackedGalleryThumbnail';
import { LanguageKeys } from 'src/common/constants/languages';
import useDeferredFileUsage from 'src/hooks/useDeferredFileUsage';
import { emptyLocalizedString } from 'src/helpers/localizedStringUtils';
import { UploadedFile } from 'src/interfaces/FileManager';
import { IImageBlock, IPageBlock, IPageBlockImageItem } from 'src/interfaces/PageBlock';
import LanguageFlagSwitcher from 'src/pages/Dashboard/System/Pages/components/LanguageFlagSwitcher';

interface Props {
  block: IImageBlock;
  contentKey: string;
  pageContentId?: string;
  selectedEditorLang: LanguageKeys;
  setSelectedEditorLang: (lang: LanguageKeys) => void;
  onChange: (patch: Partial<IPageBlock>) => void;
}

const localizedInputSlotProps = (
  lang: LanguageKeys,
  onLangChange: (lang: LanguageKeys) => void,
) => ({
  input: {
    endAdornment: <LanguageFlagSwitcher value={lang} onChange={onLangChange} />,
  },
});

function fileToGalleryItem(file: UploadedFile): IPageBlockImageItem {
  return {
    imageUrl: file.fileUrl,
    imageFileId: file.id,
    alt: emptyLocalizedString(),
  };
}

export default function ImageBlockEditorPanel({
  block,
  contentKey,
  pageContentId,
  selectedEditorLang,
  setSelectedEditorLang,
  onChange,
}: Props) {
  const { t } = useTranslation();
  const [mediaOpen, setMediaOpen] = useState(false);
  const { addPendingFile } = useDeferredFileUsage({
    contentType: 'pageContent',
    contentId: pageContentId || contentKey,
    fieldName: `block-${block.id}`,
  });

  const display = block.display ?? {};
  const isGallery = block.mode === 'gallery';
  const images = block.images ?? [];

  const trackFile = (file: UploadedFile) => {
    if (file.uploadEndpoint !== 'image') return;
    addPendingFile(file.id);
  };

  const handleSingleSelect = (file: UploadedFile) => {
    if (file.uploadEndpoint !== 'image') return;
    trackFile(file);
    onChange({
      imageUrl: file.fileUrl,
      imageFileId: file.id,
    } as Partial<IImageBlock>);
  };

  const handleGallerySelectMultiple = (files: UploadedFile[]) => {
    const imageFiles = files.filter(file => file.uploadEndpoint === 'image');
    imageFiles.forEach(trackFile);
    onChange({
      images: [...images, ...imageFiles.map(fileToGalleryItem)],
    } as Partial<IImageBlock>);
  };

  const updateDisplay = (patch: Partial<IImageBlock['display']>) => {
    onChange({ display: { ...display, ...patch } } as Partial<IImageBlock>);
  };

  const updateGalleryItem = (index: number, patch: Partial<IPageBlockImageItem>) => {
    const next = images.map((item, i) => (i === index ? { ...item, ...patch } : item));
    onChange({ images: next } as Partial<IImageBlock>);
  };

  const removeGalleryItem = (index: number) => {
    onChange({ images: images.filter((_, i) => i !== index) } as Partial<IImageBlock>);
  };

  const moveGalleryItem = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= images.length) return;
    const next = [...images];
    const current = next[index];
    const swap = next[target];
    if (!current || !swap) return;
    next[index] = swap;
    next[target] = current;
    onChange({ images: next } as Partial<IImageBlock>);
  };

  return (
    <Box sx={{ mt: 1, display: 'grid', gap: 1.5 }}>
      <FormControl fullWidth size="small">
        <InputLabel>{t('system.pages.imageBlock.mode')}</InputLabel>
        <Select
          label={t('system.pages.imageBlock.mode')}
          value={block.mode}
          onChange={e =>
            onChange({ mode: e.target.value as IImageBlock['mode'] } as Partial<IImageBlock>)
          }
        >
          <MenuItem value="single">{t('system.pages.imageBlock.modeSingle')}</MenuItem>
          <MenuItem value="gallery">{t('system.pages.imageBlock.modeGallery')}</MenuItem>
        </Select>
      </FormControl>

      {!isGallery ? (
        <>
          {block.imageUrl ? (
            <CardMedia
              component="img"
              src={block.imageUrl}
              sx={{ maxHeight: 160, objectFit: 'contain', alignSelf: 'flex-start' }}
            />
          ) : null}
          <Button variant="outlined" size="small" onClick={() => setMediaOpen(true)}>
            {t('system.pages.imageBlock.selectImage')}
          </Button>
          <TextField
            size="small"
            fullWidth
            label={t('editor.imageEdit.alt')}
            value={block.alt?.[selectedEditorLang] ?? ''}
            onChange={e =>
              onChange({
                alt: { ...(block.alt ?? emptyLocalizedString()), [selectedEditorLang]: e.target.value },
              } as Partial<IImageBlock>)
            }
            slotProps={localizedInputSlotProps(selectedEditorLang, setSelectedEditorLang)}
          />
        </>
      ) : (
        <>
          <Button variant="outlined" size="small" onClick={() => setMediaOpen(true)}>
            {t('system.pages.imageBlock.addGalleryImages')}
          </Button>
          {images.length > 0 ? (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                <StackedGalleryThumbnail
                  images={images.map(item => ({
                    src: item.imageUrl,
                    alt: item.alt?.[selectedEditorLang] ?? '',
                  }))}
                  display={display}
                  interactive={false}
                />
              </Box>
              {images.map((item, index) => (
                <Box
                  key={`${item.imageFileId ?? item.imageUrl}-${index}`}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '72px 1fr auto',
                    gap: 1,
                    alignItems: 'center',
                    p: 1,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <CardMedia
                    component="img"
                    src={item.imageUrl}
                    sx={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 1 }}
                  />
                  <TextField
                    size="small"
                    fullWidth
                    label={t('editor.imageEdit.alt')}
                    value={item.alt?.[selectedEditorLang] ?? ''}
                    onChange={e =>
                      updateGalleryItem(index, {
                        alt: {
                          ...(item.alt ?? emptyLocalizedString()),
                          [selectedEditorLang]: e.target.value,
                        },
                      })
                    }
                    slotProps={localizedInputSlotProps(selectedEditorLang, setSelectedEditorLang)}
                  />
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <IconButton
                      size="small"
                      aria-label={t('system.pages.imageBlock.moveUp')}
                      disabled={index === 0}
                      onClick={() => moveGalleryItem(index, -1)}
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label={t('system.pages.imageBlock.moveDown')}
                      disabled={index === images.length - 1}
                      onClick={() => moveGalleryItem(index, 1)}
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label={t('base.delete')}
                      onClick={() => removeGalleryItem(index)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('system.pages.imageBlock.galleryEmpty')}
            </Typography>
          )}
        </>
      )}

      <Typography variant="caption" color="text.secondary">
        {t('system.pages.imageBlock.displayOptions')}
      </Typography>
      <ImageDisplayFields
        value={display}
        onChange={updateDisplay}
        showTitle={!isGallery}
      />

      <MediaManagerModal
        open={mediaOpen}
        onClose={() => setMediaOpen(false)}
        onSelect={handleSingleSelect}
        onSelectMultiple={handleGallerySelectMultiple}
        multiple={isGallery}
        mode="select"
        endpoint="image"
        title={
          isGallery
            ? t('system.pages.imageBlock.addGalleryImages')
            : t('system.pages.imageBlock.selectImage')
        }
        showUploadButton
      />
    </Box>
  );
}
